import React, { useState } from 'react'
import { Button, Dialog, Classes, Intent } from '@blueprintjs/core'
import { get } from 'lodash'
import { parseHTMLTxtWithLinks } from './html'
import api, { IBooking, ISettings } from '@api'
import { Flex, Toaster } from '@ui'
import { useAppData } from '@providers'

import { useReload } from '@hooks/reloadContext'
import css from './HealthCheckContent.module.scss'
import { ApolloError } from 'apollo-client'
import { DateUtil, TIME } from '@utils'

const WhenWhere = ({
  startDate,
  endDate,
  address,
}: {
  startDate: DateUtil
  endDate: DateUtil
  address: string
}) => (
  <div className={css.marginBtm}>
    <div className={css.deskInfoHeader}>Desk Info</div>
    <div className={css.deskInfoClass}>
      {startDate.format('MMM d, h:mma')} - {endDate.format('MMM d, h:mma')}{' '}
    </div>
    <div className={css.deskInfoClass}>{address}</div>
  </div>
)

enum Tabs {
  HEALTH_CHECK,
  FOLLOW_UP,
}

const HealthCheckContent = ({
  onHide,
  booking,
  settings,
}: {
  onHide: () => void
  booking: IBooking | undefined
  settings: ISettings
}) => {
  const { bookings } = useReload()
  const { locationHealthCheckGroups } = useAppData()
  const [tab, setTab] = useState(Tabs.HEALTH_CHECK)
  const timezone = get(booking, 'space.floor.building.timezone', DateUtil.defaultTz())
  const address = get(booking, 'space.floor.building.address', DateUtil.defaultTz())
  const nowDate = DateUtil.tz(timezone)
  const startDate = DateUtil.tz(booking?.startDate, timezone)
  const endDate = DateUtil.tz(booking?.endDate, timezone)
  const defaultHealthScreeningConfirmButtonText = settings.healthScreeningConfirmButtonText || ''

  const defaultHealthScreeningMessage = settings.healthScreeningMessage
  const hoursBeforeBookingCheckin = settings.hoursBeforeBookingCheckin || 1
  const cannotCheckIn = !nowDate.isAfter(
    startDate.clone().sub(hoursBeforeBookingCheckin, TIME.HOURS)
  )
  const locationHealthCheckId = get(booking?.healthCheck, 'locationHeathCheckGroup.id')

  const locationHealthCheckGroup = locationHealthCheckGroups.find(
    ({ id }: { id: string }) => locationHealthCheckId === id
  )

  let healthCheckText = defaultHealthScreeningMessage

  let healthCheckButtonText = defaultHealthScreeningConfirmButtonText

  if (locationHealthCheckGroup) {
    healthCheckText = locationHealthCheckGroup?.healthScreeningOverride
      ? locationHealthCheckGroup.healthScreeningMessage || healthCheckText
      : healthCheckText

    healthCheckButtonText = locationHealthCheckGroup?.healthScreeningOverride
      ? locationHealthCheckGroup.healthScreeningConfirmButtonText || healthCheckButtonText
      : healthCheckButtonText
  }

  const confirmHealthCheck = (
    id: string | undefined,
    alsoCheckedIn: boolean,
    followUpResponse: boolean
  ) => {
    api.booking
      .confirmHealthCheck({ id, checkedIn: alsoCheckedIn, followUpResponse })
      .then(() => {
        bookings()
      })
      .catch((error: ApolloError) => {
        Toaster.fail({ error })
      })
  }

  return (
    <Dialog
      isOpen
      onClose={onHide}
      isCloseButtonShown={false}
      canOutsideClickClose={true}
      title={tab === Tabs.HEALTH_CHECK ? 'Health Check' : 'Post health check Question'}>
      {tab === Tabs.HEALTH_CHECK && (
        <>
          <div className={Classes.DIALOG_BODY}>
            <WhenWhere startDate={startDate} endDate={endDate} address={address} />
            <div style={{ whiteSpace: 'pre-wrap' }}>{parseHTMLTxtWithLinks(healthCheckText)}</div>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <Flex align="stretch" justify="flex-end">
              <div style={{ marginRight: '8px' }}>
                <Button onClick={onHide}>Cancel</Button>
              </div>
              <Button
                intent={Intent.PRIMARY}
                onClick={
                  locationHealthCheckGroup && locationHealthCheckGroup.followUpQuestionEnabled
                    ? () => setTab(1)
                    : () => {
                        confirmHealthCheck(booking?.id, !cannotCheckIn, false)
                        onHide()
                      }
                }>
                {healthCheckButtonText}
              </Button>
            </Flex>
          </div>
        </>
      )}
      {tab === Tabs.FOLLOW_UP && (
        <>
          <div className={Classes.DIALOG_BODY}>
            <WhenWhere startDate={startDate} endDate={endDate} address={address} />
            {parseHTMLTxtWithLinks(locationHealthCheckGroup?.followUpQuestionText)}
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <Flex align="stretch" justify="flex-end">
              <div style={{ marginRight: '8px' }}>
                <Button
                  onClick={() => {
                    confirmHealthCheck(booking?.id, !cannotCheckIn, false)
                    onHide()
                  }}>
                  No
                </Button>
              </div>
              <Button
                intent={Intent.PRIMARY}
                onClick={() => {
                  confirmHealthCheck(booking?.id, !cannotCheckIn, true)
                  onHide()
                }}>
                Yes
              </Button>
            </Flex>
          </div>
        </>
      )}
    </Dialog>
  )
}

export default HealthCheckContent
